import { isFormControlType } from '@generated';
import { FormHtml } from '~/shared/forms/controls/base-controls/FormHtml';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import type { FormControlModelExtended, FormControlOptionModelExtended } from './types';
import { BodyStructureControlArray } from '../concepts/BodyStructureControlArray';
import { CheckboxFormField } from '~/shared/forms/controls/fields/CheckboxFormField';
import { BodyStructureSearchFormField } from '../concepts/BodyStructureSearchFormField';
import { FormFieldContainer } from './FormFieldContainer';
import { DashTextFormField } from '~/shared/forms/controls/fields/text-fields/DashTextFormField';
import { DateFormField } from '~/shared/forms/controls/fields/date-fields/DateFormField';
import { DatePickerFormField } from '~/shared/forms/controls/fields/date-fields/DatePickerFormField';
import { DateTimeFormField } from '~/shared/forms/controls/fields/date-fields/DateTimeFormField';
import { FormLabelNoControl } from '~/shared/forms/controls/base-controls/FormLabelNoControl';
import type { RadioProps } from '~/shared/forms/controls/base-controls/Radio';
import { SpanWithHtml } from '~/shared/forms/controls/base-controls/SpanWithHtml';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { TextArrayFormField } from '~/shared/forms/controls/fields/TextArrayFormField';
import { CalculatedTextFormField } from '~/shared/forms/controls/fields/text-fields/CalculatedTextFormField';
import { RadioGroupFormFieldWithChildren } from './radio-groups/RadioGroupFormFieldWithChildren';
import { CheckboxGroupFormFieldWithChildren } from './checkbox-groups/CheckboxGroupFormFieldWithChildren';
import { Banner } from '~/shared/components/Banner';

export function FormField({ controlModel }: { controlModel: FormControlModelExtended }) {
	if (controlModel.hidden) {
		return null;
	}

	if (!isFormControlType(controlModel.controlType)) {
		throw new Error(`Control type ${controlModel.controlType} is not supported`);
	}

	const controlType = controlModel.controlType;

	const options: FormControlOptionModelExtended[] = controlModel.options
		? controlModel.options
				.filter(x => !x.hidden)
				.map(x => {
					const satisifedAnswerSuggestions = x.suggestedAnswer?.filter(x => x.when.some(y => y.isSatisified));
					const answerSuggestion =
						satisifedAnswerSuggestions && satisifedAnswerSuggestions.length > 0 ? satisifedAnswerSuggestions[0] : undefined;
					const answerSuggestionText = answerSuggestion ? answerSuggestion.when.find(x => x.isSatisified)?.reason : undefined;

					const option: FormControlOptionModelExtended = {
						...x,
						type: 'dynamic-option',
						label: x.formControls && x.formControls.length > 0 ? x.label + ':' : x.label,
						children:
							x.formControls && x.formControls.length > 0
								? x.formControls?.map((y, index) => {
										return <FormField key={`${y.name ?? y.controlType}-${index}`} controlModel={y} />;
								  })
								: null,
						isSuggested: !!answerSuggestion,
						suggestionText: answerSuggestionText,
						suggestedDiagnoses: x.suggestedDiagnoses
					};

					return option;
				})
		: [];

	switch (controlType) {
		case 'bodyStructureControlArray':
			return <BodyStructureControlArray controlModel={controlModel} />;
		case 'checkbox':
		case 'checkboxOneOrZero':
			return <CheckboxFormField {...controlModel} name={controlModel.name!} useOneAndZero={controlType === 'checkboxOneOrZero'} />;
		case 'checkboxGroup':
		case 'findings':
			return <CheckboxGroupFormFieldWithChildren {...controlModel} name={controlModel.name!} options={options} />;
		case 'findingSites':
			return <BodyStructureSearchFormField {...controlModel} name={controlModel.name!} />;
		case 'container':
			return <FormFieldContainer controlModel={controlModel} />;
		case 'dashTextbox':
			return <DashTextFormField {...controlModel} name={controlModel.name!} />;
		case 'date':
			return <DateFormField granularity="day" {...controlModel} name={controlModel.name!} />;
		case 'datePicker':
			return <DatePickerFormField {...controlModel} name={controlModel.name!} />;
		case 'dateTime':
			return <DateTimeFormField granularity="day" {...controlModel} name={controlModel.name!} />;
		case 'html':
			return <FormHtml label={controlModel.label!} className={controlModel.className} />;
		case 'image':
			return <img src={controlModel.url} alt="" />;
		case 'label':
			return <FormLabelNoControl {...controlModel} />;
		case 'radioGroup':
		case 'horizontalRadioGroup':
			return (
				<RadioGroupFormFieldWithChildren
					{...controlModel}
					name={controlModel.name!}
					options={options}
					size={controlModel.size as RadioProps['size'] | undefined}
					orientation={controlType === 'horizontalRadioGroup' ? 'horizontal' : 'vertical'}
				/>
			);
		case 'select':
			return (
				<SelectFormField
					{...controlModel}
					name={controlModel.name!}
					options={options.map(x => ({ id: x.value, name: x.label, description: x.description }))}
				/>
			);
		case 'text':
			if (!controlModel.label) {
				console.error('Text control must have a label defined');
				throw new Error('Text control must have a label defined');
			}
			return <SpanWithHtml label={controlModel.label} className={controlModel.className} />;
		case 'textArea':
			return <TextAreaFormField {...controlModel} name={controlModel.name!} />;
		case 'textbox':
			return <TextFormField {...controlModel} name={controlModel.name!} />;
		case 'textboxArray':
			return <TextArrayFormField {...controlModel} name={controlModel.name!} />;
		case 'textboxCalculated':
			return <CalculatedTextFormField {...controlModel} name={controlModel.name!} />;
		case 'warning':
			return <Banner intent="warning" title={controlModel.label || 'Warning'} message={controlModel.subLabel!} />;
		default:
			console.error('Control type not supported', controlType);
			throw new Error(`Control type ${controlType} is not supported`);
	}
}
